import React, {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  useEffect,
} from 'react';
import { authenticatedFetch } from 'utils/authenticatedFetch';
import { v4 as uuidv4 } from 'uuid';
import { io, Socket } from 'socket.io-client';
import socketServiceManager from 'app/socketService';

export enum LLMProvider {
  OPENAI = 'openai',
  ANTHROPIC = 'anthropic',
}

interface EmailSection {
  id: string;
  name: string;
  content?: string;
  defaultContent?: string;
  required?: boolean;
}

interface MCPEmailTemplate {
  id: string;
  name: string;
  currentSection: string;
  structure: {
    sections: EmailSection[];
    generatedHtml?: string;
  };
}

export interface TokenUsage {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  promptCost: number;
  completionCost: number;
  cost: number;
}
export interface AgentMessage {
  function_name: string;
  execution_time_ms: number;
  message: string;
}

interface ChatResponse {
  message: string;
  action?: string;
  updates?: {
    template?: {
      structure: {
        sections: EmailSection[];
        generatedHtml: string;
      };
    };
  };
  tokenUsage?: TokenUsage;
}

interface MCPState {
  context: {
    template: MCPEmailTemplate;
    sections: Record<string, string>;
    metadata: {
      lastUpdated: string;
      completedSections: string[];
    };
  };
  messages: Array<{
    role: 'user' | 'assistant' | 'system';
    content: string;
    bgcolor?: string;
    processingType?: string;
  }>;
  tokenUsage: TokenUsage;
  llmProvider: LLMProvider;
  dealership: any;
  agentMessage?: AgentMessage;
  next_action?: string;
  preview_mode?: string;
  conversationId: string;
}

type MCPAction =
  | { type: 'UPDATE_CONTEXT'; payload: Partial<MCPState['context']> }
  | {
      type: 'ADD_MESSAGE';
      payload: {
        role: 'user' | 'assistant' | 'system';
        content: string;
        bgcolor?: string;
        processingType?: string;
      };
    }
  | { type: 'RESET_MESSAGES' }
  | {
      type: 'UPDATE_SECTION_CONTENT';
      payload: { sectionId: string; content: string };
    }
  | {
      type: 'UPDATE_TEMPLATE_SECTIONS';
      payload: {
        sections: EmailSection[];
        generatedHtml?: string;
      };
    }
  | { type: 'UPDATE_TOKEN_USAGE'; payload: TokenUsage }
  | { type: 'UPDATE_AGENT_MESSAGE'; payload: AgentMessage }
  | { type: 'UPDATE_LLM_PROVIDER'; payload: LLMProvider }
  | { type: 'SET_TEMPLATE'; payload: string }
  | { type: 'UPDATE_NEXT_ACTION'; payload: string }
  | { type: 'SET_DEALERSHIP'; payload: any }
  | { type: 'SET_PREVIEW_MODE'; payload: string }
  | { type: 'START_NEW_CONVERSATION' };

const initialState: MCPState = {
  context: {
    template: {
      id: 'default',
      name: 'Default Template',
      currentSection: 'default',
      structure: {
        sections: [],
        generatedHtml: '',
      },
    },
    sections: {
      offers: '',
      inventory: '',
    },
    metadata: {
      lastUpdated: new Date().toISOString(),
      completedSections: [],
    },
  },
  messages: [],
  tokenUsage: {
    promptTokens: 0,
    completionTokens: 0,
    totalTokens: 0,
    promptCost: 0,
    completionCost: 0,
    cost: 0,
  },
  llmProvider: LLMProvider.OPENAI,
  dealership: null,
  next_action: '',
  conversationId: uuidv4(), // Use uuidv4 for unique conversation ID
};

const mcpReducer = (state: MCPState, action: MCPAction): MCPState => {
  switch (action.type) {
    case 'UPDATE_CONTEXT':
      return {
        ...state,
        context: {
          ...state.context,
          ...action.payload,
        },
      };
    case 'ADD_MESSAGE':
      const updatedState = {
        ...state,
        messages: [...state.messages, action.payload],
      };
      return updatedState;
    case 'RESET_MESSAGES':
      return {
        ...state,
        tokenUsage: {
          promptTokens: 0,
          completionTokens: 0,
          totalTokens: 0,
          promptCost: 0,
          completionCost: 0,
          cost: 0,
        },
        messages: [],
        context: {
          ...state.context,
          template: {
            id: '',
            name: '',
            currentSection: '',
            structure: {
              sections: [],
              generatedHtml: '',
            },
          },
          sections: {},
          metadata: {
            lastUpdated: new Date().toISOString(),
            completedSections: [],
          },
        },
        conversationId: uuidv4(), // Use uuidv4 for unique conversation ID
      };
    case 'UPDATE_SECTION_CONTENT':
      return {
        ...state,
        context: {
          ...state.context,
          sections: {
            ...state.context.sections,
            [action.payload.sectionId]: action.payload.content,
          },
        },
      };
    case 'UPDATE_TEMPLATE_SECTIONS':
      // Generate HTML from sections if no generatedHtml provided
      let generatedHtml = action.payload.generatedHtml;
      if (!generatedHtml && action.payload.sections) {
        generatedHtml = action.payload.sections
          .map(section => section.content || section.defaultContent || '')
          .filter(content => content.trim())
          .join('\n\n');
      }

      return {
        ...state,
        context: {
          ...state.context,
          template: {
            ...state.context.template,
            structure: {
              sections:
                action.payload.sections ||
                state.context.template.structure.sections,
              generatedHtml:
                generatedHtml || state.context.template.structure.generatedHtml,
            },
          },
        },
      };
    case 'UPDATE_TOKEN_USAGE':
      return {
        ...state,
        tokenUsage: {
          promptTokens:
            state.tokenUsage.promptTokens + action.payload.promptTokens,
          completionTokens:
            state.tokenUsage.completionTokens + action.payload.completionTokens,
          totalTokens:
            state.tokenUsage.totalTokens + action.payload.totalTokens,
          promptCost: state.tokenUsage.promptCost + action.payload.promptCost,
          completionCost:
            state.tokenUsage.completionCost + action.payload.completionCost,
          cost: state.tokenUsage.cost + action.payload.cost,
        },
      };
    case 'UPDATE_AGENT_MESSAGE':
      return {
        ...state,
        agentMessage: action.payload,
      };
    case 'UPDATE_LLM_PROVIDER':
      return {
        ...state,
        llmProvider: action.payload,
      };
    case 'UPDATE_NEXT_ACTION':
      return {
        ...state,
        next_action: action.payload,
      };
    case 'SET_TEMPLATE':
      return {
        ...state,
        context: {
          ...state.context,
          template: {
            ...state.context.template,
            id: action.payload,
            name: action.payload,
            currentSection: 'default',
            structure: {
              sections: [],
              generatedHtml: '',
            },
          },
        },
      };
    case 'SET_DEALERSHIP':
      return {
        ...state,
        dealership: action.payload,
      };
    case 'SET_PREVIEW_MODE':
      return {
        ...state,
        preview_mode: action.payload,
      };
    case 'START_NEW_CONVERSATION':
      const newState = {
        ...state,
        messages: [],
        tokenUsage: {
          promptTokens: 0,
          completionTokens: 0,
          totalTokens: 0,
          promptCost: 0,
          completionCost: 0,
          cost: 0,
        },
        context: {
          ...state.context,
          template: {
            id: 'default',
            name: 'Default Template',
            currentSection: 'default',
            structure: {
              sections: [],
              generatedHtml: '',
            },
          },
          sections: {},
          metadata: {
            lastUpdated: new Date().toISOString(),
            completedSections: [],
          },
        },
        conversationId: uuidv4(), // Use uuidv4 for unique conversation ID
        agentMessage: undefined,
        next_action: '',
        preview_mode: undefined,
      };
      return newState;
    default:
      return state;
  }
};

interface MCPContextType {
  state: MCPState;
  dispatch: React.Dispatch<MCPAction>;
  updateContext: (updates: Partial<MCPState['context']>) => void;
  addMessage: (role: 'user' | 'assistant' | 'system', content: string, bgcolor?: string, processingType?: string) => void;
  resetMessages: () => void;
  sendMessage: (content: string, role?: string, fromUser?: boolean) => Promise<ChatResponse>;
  startNewConversation: () => void;
  addInitialGreeting: () => void;
}

const MCPContext = createContext<MCPContextType | null>(null);

interface MCPProviderProps {
  children: ReactNode;
  dealership?: any;
}

export const MCPProvider: React.FC<MCPProviderProps> = ({
  children,
  dealership,
}) => {
  const [state, dispatch] = useReducer(mcpReducer, initialState);
  const accumulatedEmailContentRef = React.useRef('');
  const socketRef = React.useRef<any>(null);
  const greetingAddedRef = React.useRef(false);
  const isConnectingRef = React.useRef(false);

  // Effect to handle dealer changes and reset everything
  useEffect(() => {
    if (dealership) {
      // Set connecting flag to prevent multiple connections
      isConnectingRef.current = true;

      // Disconnect existing WebSocket if any
      if (socketRef.current) {
        socketRef.current.emit('unsubscribe', state.conversationId);
        socketRef.current.disconnect();
        socketRef.current = null;
      }

      // Reset accumulated email content and greeting flag
      accumulatedEmailContentRef.current = '';
      greetingAddedRef.current = false;

      // Set dealership and start new conversation with a small delay to ensure clean disconnection
      dispatch({ type: 'SET_DEALERSHIP', payload: dealership });
      dispatch({ type: 'SET_TEMPLATE', payload: 'default' });

      // Small delay to ensure WebSocket disconnection is complete
      setTimeout(() => {
        startNewConversation();
      }, 100);
    }
  }, [dealership?._id]); // Use dealer ID as dependency to trigger on dealer change

  // Effect to set up WebSocket connection when conversationId changes
  useEffect(() => {
    if (!state.conversationId || !dealership) {
      return;
    }

    // Prevent multiple simultaneous connections
    if (isConnectingRef.current) {
      return;
    }

    // Check if socket already exists and is connected
    if (socketRef.current && socketRef.current.connected) {
      return;
    }

    // Add a small delay to prevent race condition with dealer change effect
    const timeoutId = setTimeout(() => {
      let ws_url = process.env.REACT_APP_WS_URL;

      if (!ws_url) {
        console.error('WebSocket URL is not defined');
        return;
      }

      // Double-check connecting flag after delay
      if (isConnectingRef.current) {
        return;
      }

      // Set connecting flag
      isConnectingRef.current = true;

      // Use global socket from socketServiceManager instead of creating new one
      let socket: Socket | null = socketServiceManager.getGlobalSocket();
      
      if (!socket) {
        // Initialize global socket if it doesn't exist
        const socketConfig = {
          url: ws_url,
          autoConnect: true,
          reconnection: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 1000,
          options: {
            path: '/ws/',
            transports: ['websocket', 'polling'],
          }
        };
        socketServiceManager.initializeGlobalSocket(socketConfig);
        
        // Wait a bit for the global socket to be created
        setTimeout(() => {
          socket = socketServiceManager.getGlobalSocket();
          if (socket) {
            setupSocketListeners(socket);
          } else {
            isConnectingRef.current = false;
            console.error('[SocketIO] Failed to initialize global socket');
          }
        }, 100);
      } else {
        setupSocketListeners(socket);
      }

      function setupSocketListeners(socket: Socket) {
        socketRef.current = socket;

        socket.on('connect', () => {
          // Reset connecting flag
          isConnectingRef.current = false;
          // subscribe to your conversation room
          socket.emit('subscribe', state.conversationId);
        });

        socket.on('disconnect', () => {
          // Reset connecting flag
          isConnectingRef.current = false;
        });

        socket.on('token_usage', payload => {
          dispatch({ type: 'UPDATE_TOKEN_USAGE', payload: payload.usage });
        });

        socket.on('agent_message', payload => {
          dispatch({ type: 'UPDATE_AGENT_MESSAGE', payload: payload.message });
        });

        socket.on('processing_status', payload => {
          let bgcolor = '#e6f4ea';
          // Extract processing type from the payload
          const processingType = payload?.message?.processing_type || 'processing';
          addMessage('assistant', payload?.message?.text, bgcolor, processingType);
        });

        socket.on('streaming_response', payload => {
          accumulatedEmailContentRef.current += payload?.message?.text || '';
          dispatch({ type: 'SET_PREVIEW_MODE', payload: 'html' });
          dispatch({
            type: 'UPDATE_TEMPLATE_SECTIONS',
            payload: {
              sections: payload?.updates?.template?.structure?.sections,
              generatedHtml: accumulatedEmailContentRef.current,
            },
          });
          if (payload.isFinal) {
            dispatch({ type: 'SET_PREVIEW_MODE', payload: 'preview' });
          }
        });

        socket.on('connect_error', error => {
          console.error('[SocketIO] Connection error:', error);
          // Reset connecting flag on error
          isConnectingRef.current = false;
        });
      }
    }, 150); // 150ms delay to prevent race condition

    return () => {
      clearTimeout(timeoutId);
      if (socketRef.current) {
        socketRef.current.emit('unsubscribe', state.conversationId);
        // Don't disconnect global socket, just remove event listeners
        socketRef.current.off('connect');
        socketRef.current.off('disconnect');
        socketRef.current.off('token_usage');
        socketRef.current.off('agent_message');
        socketRef.current.off('processing_status');
        socketRef.current.off('streaming_response');
        socketRef.current.off('connect_error');
        socketRef.current = null;
      }
      // Reset connecting flag
      isConnectingRef.current = false;
    };
  }, [state.conversationId, dealership?._id]); // Re-run when conversationId or dealer changes

  // Effect to add initial greeting when messages are cleared and dealership is available
  useEffect(() => {
    if (
      state.messages.length === 0 &&
      state.dealership &&
      state.conversationId &&
      !greetingAddedRef.current
    ) {
      greetingAddedRef.current = true;
      addInitialGreeting();
    }
  }, [state.messages.length, state.dealership, state.conversationId]);

  const updateContext = (updates: Partial<MCPState['context']>) => {
    dispatch({ type: 'UPDATE_CONTEXT', payload: updates });
  };

  const addMessage = (
    role: 'user' | 'assistant' | 'system',
    content: string,
    bgcolor?: string,
    processingType?: string,
  ) => {
    dispatch({ type: 'ADD_MESSAGE', payload: { role, content, bgcolor, processingType } });
  };

  const resetMessages = () => {
    dispatch({ type: 'RESET_MESSAGES' });
  };

  const startNewConversation = () => {
    // Reset accumulated email content and greeting flag
    accumulatedEmailContentRef.current = '';
    greetingAddedRef.current = false;
    // Reset connecting flag to allow new connection
    isConnectingRef.current = false;
    // Start new conversation
    dispatch({ type: 'START_NEW_CONVERSATION' });
  };

  const addInitialGreeting = () => {
    if (state.dealership) {
      // Add system message with only _id and name from dealership
      const { _id, name, address, timezone } = state.dealership || {};
      addMessage(
        'system',
        JSON.stringify({
          selected_dealership: { _id, name, details: { address, timezone } },
        }),
      );
      // Show initial greeting
      addMessage(
        'assistant',
        `Hi, I'm Mia, your email generation assistant at ${state.dealership?.name} dealership. I can help generate a vehicle highlight email, lease offer email, or finance offer email. Which one would you like to start with?`,
      );
    }
  };

  const sendMessage = async (
    content: string,
    role: string = 'user',
    fromUser: boolean = true,
  ): Promise<ChatResponse> => {
    if (!content.trim()) {
      throw new Error('Message cannot be empty');
    }
    if (role === 'user') {
      addMessage('user', content);
    } else if (role === 'assistant') {
      addMessage('assistant', content);
    }

    try {
      const response = await authenticatedFetch(
        process.env.REACT_APP_API_URL + '/api/v2/chat',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            dealer_id: state.dealership?._id,
            messages: [...state.messages, { role: role, content }],
            context: state.context,
            provider: state.llmProvider,
            conversationId: state.conversationId,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || 'Failed to get response from server',
        );
      }

      const data = (await response.json()) as ChatResponse;

      // Add assistant message
      if (data.message) {
        addMessage('assistant', data.message);
      }

      // Update template sections if provided
      if (data.updates?.template?.structure) {
        dispatch({
          type: 'UPDATE_TEMPLATE_SECTIONS',
          payload: {
            sections: data.updates.template.structure.sections,
            generatedHtml: data.updates.template.structure.generatedHtml,
          },
        });
      }

      // Update token usage if provided
      if (data.tokenUsage) {
        dispatch({
          type: 'UPDATE_TOKEN_USAGE',
          payload: data.tokenUsage,
        });
      }

      //initiate email_generation request
      // Only allow assistant to auto-trigger once per user message
      if (data.action && data.action === 'GENERATE_EMAIL' && fromUser) {
        //we need to fire off another call to the api endpoint with this specific action..
        generateEmail();
      }

      return data;
    } catch (error) {
      console.error('Error sending message:', error);
      addMessage(
        'assistant',
        'Sorry, there was an error processing your request.',
      );
      throw error;
    }
  };

  const generateEmail = async () => {
    try {
      addMessage(
        'assistant',
        'Generating email using the information collected so far...',
      );
      // Reset accumulated content
      accumulatedEmailContentRef.current = '';
      dispatch({
        type: 'UPDATE_TEMPLATE_SECTIONS',
        payload: {
          sections: [],
          generatedHtml: '',
        },
      });

      const response = await authenticatedFetch(
        process.env.REACT_APP_API_URL + '/api/v2/email/generate',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            dealer_id: state.dealership?._id,
            messages: [...state.messages],
            context: state.context,
            provider: state.llmProvider,
            conversationId: state.conversationId,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Email generation failed');
      }

      const data = await response.json();

      // You can add any message returned by the tool
      if (data.message) {
        addMessage('assistant', data.message);
      }

      if (
        data.generatedHtml &&
        data.action &&
        data.action === 'DISPLAY_EMAIL'
      ) {
        dispatch({ type: 'SET_PREVIEW_MODE', payload: 'preview' });
        dispatch({
          type: 'UPDATE_TEMPLATE_SECTIONS',
          payload: {
            sections: data.sections || [],
            generatedHtml: data.generatedHtml || '',
          },
        });
      }

      return data;
    } catch (err) {
      console.error('Failed to generate email:', err);
      addMessage(
        'assistant',
        'Error generating email. Please try again later.',
      );
    }
  };

  return (
    <MCPContext.Provider
      value={{
        state,
        dispatch,
        updateContext,
        addMessage,
        resetMessages,
        sendMessage,
        startNewConversation,
        addInitialGreeting,
      }}
    >
      {children}
    </MCPContext.Provider>
  );
};

export const useMCP = () => {
  const context = useContext(MCPContext);
  if (!context) {
    throw new Error('useMCP must be used within an MCPProvider');
  }
  return context;
};
